import type { HttpClient } from './http';
import type {
  Connection,
  CreateConnectionPayload,
  CreateTaskPayload,
  Cursor,
  Id,
  Task,
  TaskGroup,
  TaskInteractionResponse,
  TaskStateDetail,
  UpdateConnectionPayload,
  UpdateTaskPayload,
  UpdateWorkflowPayload,
  Visitor,
  WorkflowGraphTask,
  WorkflowMetadata,
  WorkflowSummary,
  WorkflowVersionEvent,
  WorkflowVersionPage,
  WorkflowWebhook,
} from './types';

export interface ListWorkflowVersionsOptions {
  beforeId?: Id | null;
  limit?: number;
}

export interface WorkflowApi {
  list(): Promise<WorkflowSummary[]>;
  get(id: Id): Promise<WorkflowMetadata>;
  create(): Promise<WorkflowMetadata>;
  remove(id: Id): Promise<{ message: string } | undefined>;
  run(id: Id): Promise<{ message: string } | undefined>;
  reset(id: Id): Promise<{ message: string } | undefined>;
  update(id: Id, payload: UpdateWorkflowPayload): Promise<WorkflowMetadata>;

  // Nested: tasks
  listTasks(workflowId: Id): Promise<WorkflowGraphTask[]>;
  getTaskState(workflowId: Id, taskId: Id): Promise<TaskStateDetail>;
  createTask(workflowId: Id, payload: CreateTaskPayload): Promise<Task>;
  updateTask(
    workflowId: Id,
    taskId: Id,
    payload: UpdateTaskPayload
  ): Promise<Task>;
  deleteTask(
    workflowId: Id,
    taskId: Id
  ): Promise<{ message: string } | undefined>;
  respondToTaskInteraction(
    workflowId: Id,
    taskId: Id,
    payload: TaskInteractionResponse
  ): Promise<{ message: string } | undefined>;
  uploadTaskInteractionFile(
    workflowId: Id,
    taskId: Id,
    payload: { file: File; response?: TaskInteractionResponse }
  ): Promise<{ message: string } | undefined>;

  // Nested: task groups
  listTaskGroups(workflowId: Id): Promise<TaskGroup[]>;
  createTaskGroup(
    workflowId: Id,
    payload: {
      title: string;
      taskIds?: Id[];
      childGroupIds?: Id[];
    }
  ): Promise<TaskGroup>;
  updateTaskGroup(
    workflowId: Id,
    taskGroupId: Id,
    payload: {
      title?: string;
      taskIds?: Id[];
      childGroupIds?: Id[];
    }
  ): Promise<TaskGroup>;
  deleteTaskGroup(
    workflowId: Id,
    taskGroupId: Id
  ): Promise<{ message: string } | undefined>;

  // Nested: connections
  listConnections(workflowId: Id): Promise<Connection[]>;
  createConnection(
    workflowId: Id,
    payload: CreateConnectionPayload
  ): Promise<Connection>;
  updateConnection(
    workflowId: Id,
    connectionId: Id,
    payload: UpdateConnectionPayload
  ): Promise<Connection>;
  deleteConnection(
    workflowId: Id,
    connectionId: Id
  ): Promise<{ message: string } | undefined>;

  // Nested: visitors
  createVisitor(workflowId: Id, visitor: Visitor): Promise<Visitor>;
  updateVisitorCursor(
    workflowId: Id,
    visitorId: string,
    cursor: Cursor
  ): Promise<{ message: string } | undefined>;

  // Webhooks
  listWebhooks(workflowId: Id): Promise<WorkflowWebhook[]>;
  createWebhook(workflowId: Id): Promise<WorkflowWebhook>;
  deleteWebhook(
    workflowId: Id,
    webhookId: string
  ): Promise<{ message: string } | undefined>;

  listVersions(
    workflowId: Id,
    options?: ListWorkflowVersionsOptions
  ): Promise<WorkflowVersionPage>;
  restoreVersion(
    workflowId: Id,
    versionId: Id
  ): Promise<{ restoredEvents: WorkflowVersionEvent[] }>;
}

export function createWorkflowsApi(http: HttpClient): WorkflowApi {
  const base = '/workflows';

  return {
    list: () => http.get<WorkflowSummary[]>(`${base}`),
    get: (id) => http.get<WorkflowMetadata>(`${base}/${id}`),
    create: () => http.post<WorkflowMetadata>(`${base}`, {}),
    remove: (id) => http.delete(`${base}/${id}`),
    run: (id) =>
      http.post<{ message: string } | undefined>(`${base}/${id}/run`, {}),
    reset: (id) =>
      http.post<{ message: string } | undefined>(`${base}/${id}/reset`, {}),
    update: (id, payload) =>
      http.patch<WorkflowMetadata>(`${base}/${id}`, payload),

    // tasks
    listTasks: (workflowId) =>
      http.get<WorkflowGraphTask[]>(`${base}/${workflowId}/tasks`),
    getTaskState: (workflowId, taskId) =>
      http.get<TaskStateDetail>(`${base}/${workflowId}/tasks/${taskId}/state`),
    createTask: (workflowId, payload) =>
      http.post<Task>(`${base}/${workflowId}/tasks`, payload),
    updateTask: (workflowId, taskId, payload) =>
      http.put<Task>(`${base}/${workflowId}/tasks/${taskId}`, payload),
    deleteTask: (workflowId, taskId) =>
      http.delete(`${base}/${workflowId}/tasks/${taskId}`),
    respondToTaskInteraction: (workflowId, taskId, payload) =>
      http.post<{ message: string } | undefined>(
        `${base}/${workflowId}/tasks/${taskId}/interaction`,
        payload
      ),
    uploadTaskInteractionFile: (workflowId, taskId, payload) => {
      const formData = new FormData();
      formData.append('file', payload.file);
      formData.append('response', JSON.stringify(payload.response ?? {}));
      return http.post<{ message: string } | undefined>(
        `${base}/${workflowId}/tasks/${taskId}/interaction/upload`,
        formData
      );
    },

    // task groups
    listTaskGroups: (workflowId) =>
      http.get<TaskGroup[]>(`${base}/${workflowId}/task-groups`),
    createTaskGroup: (workflowId, payload) =>
      http.post<TaskGroup>(`${base}/${workflowId}/task-groups`, payload),
    updateTaskGroup: (workflowId, taskGroupId, payload) =>
      http.put<TaskGroup>(
        `${base}/${workflowId}/task-groups/${taskGroupId}`,
        payload
      ),
    deleteTaskGroup: (workflowId, taskGroupId) =>
      http.delete<{ message: string } | undefined>(
        `${base}/${workflowId}/task-groups/${taskGroupId}`
      ),

    // connections
    listConnections: (workflowId) =>
      http.get<Connection[]>(`${base}/${workflowId}/connections`),
    createConnection: (workflowId, payload) =>
      http.post<Connection>(`${base}/${workflowId}/connections`, payload),
    updateConnection: (workflowId, connectionId, payload) =>
      http.put<Connection>(
        `${base}/${workflowId}/connections/${connectionId}`,
        payload
      ),
    deleteConnection: (workflowId, connectionId) =>
      http.delete(`${base}/${workflowId}/connections/${connectionId}`),

    // visitors
    createVisitor: (workflowId, visitor) =>
      http.post<Visitor>(`${base}/${workflowId}/visitors`, visitor),
    updateVisitorCursor: (workflowId, visitorId, cursor) =>
      http.put<{ message: string } | undefined>(
        `${base}/${workflowId}/visitors/${visitorId}/cursor`,
        cursor
      ),

    // webhooks
    listWebhooks: (workflowId) =>
      http.get<WorkflowWebhook[]>(`${base}/${workflowId}/webhooks`),
    createWebhook: (workflowId) =>
      http.post<WorkflowWebhook>(`${base}/${workflowId}/webhooks`, {}),
    deleteWebhook: (workflowId, webhookId) =>
      http.delete<{ message: string } | undefined>(
        `${base}/${workflowId}/webhooks/${webhookId}`
      ),

    listVersions: (workflowId, options) => {
      const params = new URLSearchParams();
      if (options?.beforeId != null) {
        params.set('beforeId', String(options.beforeId));
      }
      params.set('limit', String(options?.limit ?? 50));
      const query = params.toString();
      return http.get<WorkflowVersionPage>(
        `${base}/${workflowId}/versions${query ? `?${query}` : ''}`
      );
    },
    restoreVersion: (workflowId, versionId) =>
      http.post<{ restoredEvents: WorkflowVersionEvent[] }>(
        `${base}/${workflowId}/versions/${versionId}/restore`,
        {}
      ),
  };
}
